/** Display names for engine ids in the Prashna chart detail (EN + Devanagari HI).
 *
 * Planets and rashis read the governed domain-data maps directly. The nakshatra
 * map is a 27-entry mirror of `@ishvaram/domain-data/nakshatras` kept local so
 * the client bundle does not ship that 32KB table for two words;
 * `PrashnaChartLabels.test.ts` proves the mirror equals the source. Unknown ids
 * fall back to the raw engine value rather than to a guess.
 */

import { planetMap } from "@ishvaram/domain-data/planets";
import { rashiMap } from "@ishvaram/domain-data/rashis";

/** @internal */
export const NAKSHATRA_NAMES: Readonly<Record<string, { en: string; hi: string }>> = {
  ashwini: { en: "Ashwini", hi: "अश्विनी" },
  bharani: { en: "Bharani", hi: "भरणी" },
  krittika: { en: "Krittika", hi: "कृत्तिका" },
  rohini: { en: "Rohini", hi: "रोहिणी" },
  mrigashira: { en: "Mrigashira", hi: "मृगशिरा" },
  ardra: { en: "Ardra", hi: "आर्द्रा" },
  punarvasu: { en: "Punarvasu", hi: "पुनर्वसु" },
  pushya: { en: "Pushya", hi: "पुष्य" },
  ashlesha: { en: "Ashlesha", hi: "आश्लेषा" },
  magha: { en: "Magha", hi: "मघा" },
  purva_phalguni: { en: "Purva Phalguni", hi: "पूर्व फाल्गुनी" },
  uttara_phalguni: { en: "Uttara Phalguni", hi: "उत्तर फाल्गुनी" },
  hasta: { en: "Hasta", hi: "हस्त" },
  chitra: { en: "Chitra", hi: "चित्रा" },
  swati: { en: "Swati", hi: "स्वाति" },
  vishakha: { en: "Vishakha", hi: "विशाखा" },
  anuradha: { en: "Anuradha", hi: "अनुराधा" },
  jyeshtha: { en: "Jyeshtha", hi: "ज्येष्ठा" },
  moola: { en: "Moola", hi: "मूल" },
  purva_ashadha: { en: "Purva Ashadha", hi: "पूर्वाषाढ़ा" },
  uttara_ashadha: { en: "Uttara Ashadha", hi: "उत्तराषाढ़ा" },
  shravana: { en: "Shravana", hi: "श्रवण" },
  dhanishta: { en: "Dhanishta", hi: "धनिष्ठा" },
  shatabhisha: { en: "Shatabhisha", hi: "शतभिषा" },
  purva_bhadrapada: { en: "Purva Bhadrapada", hi: "पूर्व भाद्रपद" },
  uttara_bhadrapada: { en: "Uttara Bhadrapada", hi: "उत्तर भाद्रपद" },
  revati: { en: "Revati", hi: "रेवती" },
};

export function planetName(id: string, isHi: boolean): string {
  const planet = planetMap[id];
  if (!planet) return id;
  return isHi ? planet.name_hi : planet.name_en;
}

/** "Makar (Capricorn)" in English — the Vedic id the rest of the result shows,
 * glossed — and "मकर" in Hindi. */
export function rashiName(id: string, isHi: boolean): string {
  const rashi = rashiMap[id];
  if (!rashi) return id;
  if (isHi) return rashi.name_hi;
  return `${id.charAt(0).toUpperCase()}${id.slice(1)} (${rashi.name_en})`;
}

export function nakshatraName(id: string, isHi: boolean): string {
  const nakshatra = NAKSHATRA_NAMES[id];
  if (!nakshatra) return id;
  return isHi ? nakshatra.hi : nakshatra.en;
}

/** Degrees within the sign as d°m′ — the precision a reader can check against
 * any panchang, not the engine's four decimals. */
export function formatSignDegree(degree: number): string {
  const whole = Math.floor(degree);
  const minutes = Math.floor((degree - whole) * 60);
  return `${whole}°${String(minutes).padStart(2, "0")}′`;
}
